import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Smartphone, RefreshCw, Server, ExternalLink, Users } from 'lucide-react';
import ContactManager from './ContactManager';

interface Subdomain {
  id: string;
  subdomain: string;
  domain: string;
  directory: string;
  status: string;
  created_at: string;
}

interface BroadcastContentProps {
  activeDomains: Subdomain[];
  targetDomain: string;
  targetPhone: string;
  broadcastMessage: string;
  isLoading: boolean;
  onBroadcast: (e: React.FormEvent) => void;
  onBroadcastMultiple: (phoneNumbers: string[]) => void;
  onTargetDomainChange: (value: string) => void;
  onTargetPhoneChange: (value: string) => void;
  apiCall: (endpoint: string, method?: string, data?: any) => Promise<any>;
}

const BroadcastContent: React.FC<BroadcastContentProps> = ({
  activeDomains,
  targetDomain,
  targetPhone,
  broadcastMessage,
  isLoading,
  onBroadcast,
  onBroadcastMultiple,
  onTargetDomainChange,
  onTargetPhoneChange,
  apiCall
}) => {
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [broadcastMode, setBroadcastMode] = useState<'manual' | 'contacts'>('manual');
  const [contacts, setContacts] = useState<any[]>([]);

  const handleBroadcastToContacts = async () => {
    if (selectedContacts.length === 0) {
      alert('Pilih kontak terlebih dahulu');
      return;
    }
    
    // Get phone numbers from selected contacts
    const phoneNumbers = contacts
      .filter(contact => selectedContacts.includes(contact.id))
      .map(contact => contact.phone_number);
    
    onBroadcastMultiple(phoneNumbers);
  };

  const handleContactSelectionChange = (contactIds: string[]) => {
    setSelectedContacts(contactIds);
  };

  React.useEffect(() => {
    const loadContacts = async () => {
      try {
        const result = await apiCall('/contacts');
        if (result.success) {
          setContacts(result.contacts || []);
        }
      } catch (error) {
        console.error('Error loading contacts:', error);
      }
    };
    loadContacts();
  }, [apiCall]);
  return (
    <div className="p-4 pb-20">
      {/* Mode Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
        <div className="p-4">
          <div className="flex space-x-4">
            <button
              onClick={() => setBroadcastMode('manual')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                broadcastMode === 'manual'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Smartphone className="h-4 w-4 inline mr-2" />
              Manual
            </button>
            <button
              onClick={() => setBroadcastMode('contacts')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                broadcastMode === 'contacts'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Kontak
            </button>
          </div>
        </div>
      </div>

      {/* Contact Manager */}
      {broadcastMode === 'contacts' && (
        <div className="mb-4">
          <ContactManager
            apiCall={apiCall}
            selectedContacts={selectedContacts}
            onContactSelectionChange={handleContactSelectionChange}
          />
        </div>
      )}

      {/* Broadcast Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Smartphone className="h-5 w-5 mr-2 text-blue-600" />
            {broadcastMode === 'manual' ? 'Kirim Link Domain' : 'Broadcast ke Kontak'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {broadcastMode === 'manual' 
              ? 'Kirim link domain aktif ke WhatsApp'
              : `Kirim ke ${selectedContacts.length} kontak terpilih`
            }
          </p>
        </div>
        
        <div className="p-4">
          <div className="space-y-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pilih Domain
                </label>
                <Select value={targetDomain} onValueChange={onTargetDomainChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Pilih domain yang akan dikirim" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeDomains.map((domain) => (
                      <SelectItem key={domain.id} value={`${domain.subdomain}.${domain.domain}`}>
                        {domain.subdomain}.{domain.domain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {broadcastMode === 'manual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nomor WhatsApp Tujuan
                  </label>
                  <Input
                    type="tel"
                    placeholder="628123456789"
                    value={targetPhone}
                    onChange={(e) => onTargetPhoneChange(e.target.value)}
                    className="w-full"
                    required
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pesan
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  rows={3}
                  value={broadcastMessage}
                  readOnly
                />
              </div>
            </div>
            
            {broadcastMode === 'manual' ? (
              <form onSubmit={onBroadcast}>
                <Button 
                  type="submit" 
                  disabled={isLoading || !targetDomain || !targetPhone} 
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
                >
                  {isLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                  <Smartphone className="h-4 w-4 mr-2" />
                  Kirim Link
                </Button>
              </form>
            ) : (
              <Button 
                onClick={handleBroadcastToContacts}
                disabled={isLoading || !targetDomain || selectedContacts.length === 0} 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
              >
                {isLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                <Users className="h-4 w-4 mr-2" />
                Broadcast ke {selectedContacts.length} Kontak
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active Domains */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Server className="h-5 w-5 mr-2 text-blue-600" />
            Domain Aktif
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {activeDomains.length} domain tersedia
          </p>
        </div>
        
        <div className="divide-y divide-gray-100">
          {activeDomains.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3 text-gray-400" />
              <p className="text-gray-500">Memuat domain aktif...</p>
            </div>
          ) : (
            activeDomains.map((domain) => (
              <div key={domain.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 text-sm">
                      {domain.subdomain}.{domain.domain}
                    </h3>
                    <div className="flex items-center mt-1 space-x-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        domain.status === 'active' 
                          ? 'bg-green-100 text-green-800'
                          : domain.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {domain.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(domain.created_at).toLocaleDateString('id-ID')}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`http://${domain.subdomain}.${domain.domain}`, '_blank')}
                    className="ml-3 p-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BroadcastContent;